import React, { useCallback, useMemo } from 'react';
import Link from 'next/link'

import { useRouter } from "next/router";

import styles from './index.module.scss';
import cName from "classnames";

interface IProps {
  page: number;
  total: number;
  size?: number;
  url?: string;// 例如 /exhibition/download?keyword=123&page=
  pagerCount?: 5 | 7 | 9 | 11 | 13 | 15;
}

const Pagination = ({ page, total, size = 4, url, pagerCount = 5 }: IProps) => {
  const router = useRouter();

  const getUrl = useCallback((page: number) => {
    if (!url) {
      let query = [`page=${page}`];
      for (const key in router.query) {
        if (key === 'page') continue;
        const value = router.query[key];
        query.push(`${key}=${value}`)
      }
      return `${router.pathname}?${query.join('&')}`
    }
    else {
      return `${url}${page}`
    }
  }, [url, router.query])

  const lastPage = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size])

  const pageList = useMemo(() => {
    const _list: number[] = [];
    let start = 1;
    let end = lastPage;
    if (lastPage > pagerCount + 2) {
      start = page > Math.ceil(pagerCount / 2) + 1 ? Math.max(1, page - Math.floor(pagerCount / 2)) : 1;
      end = page < lastPage - Math.ceil(pagerCount / 2) ? Math.min(lastPage, start + pagerCount - 1) : lastPage;

      if (end - start < pagerCount - 1) {
        start = end - pagerCount + 1
      }
    }

    for (let i = start; i <= end; i++) {
      _list.push(i);
    }
    return _list;
  }, [page, pagerCount, lastPage])

  const leftEllipsisVisible = useMemo(() => pageList[0] > 2, [pageList])
  const leftEllipsisPage = useMemo(() => pageList[0] - 1, [pageList])

  const rightEllipsisVisible = useMemo(() => pageList[pageList.length - 1] < lastPage - 1, [pageList, lastPage])
  const rightEllipsisPage = useMemo(() => pageList[pageList.length - 1] + 1, [pageList])

  return (
    <nav className={styles.pagination}>
      <Link href={page > 1 ? getUrl(page - 1) : 'javascript:void(0)'}>
        <a className={cName(page <= 1 ? styles.disabled : '')}>{'<'}</a>
      </Link>

      {
        leftEllipsisVisible && (
          <>
            <Link href={getUrl(1)}>
              <a>1</a>
            </Link>
            <Link href={getUrl(leftEllipsisPage)}>
              <a>{'···'}</a>
            </Link>
          </>
        )
      }

      {
        pageList.map((item) => (
          <Link key={item} href={item !== page ? getUrl(item) : 'javascript:void(0)'}>
            <a className={cName(item === page ? styles.active : '')}>{item}</a>
          </Link>
        ))
      }

      {
        rightEllipsisVisible && (
          <>
            <Link href={getUrl(rightEllipsisPage)}>
              <a>{'···'}</a>
            </Link>
            <Link href={getUrl(lastPage)}>
              <a>{lastPage}</a>
            </Link>
          </>
        )
      }

      <Link href={page < lastPage ? getUrl(page + 1) : 'javascript:void(0)'}>
        <a className={cName(page >= lastPage ? styles.disabled : '')}>{'>'}</a>
      </Link>
    </nav>
  )
}

export default Pagination